import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { GALLERY } from "@/lib/data";
import { useI18n } from "@/context/LanguageContext";

const CAT_KEY = { "Industrial Sheds": "sheds", "Storage Tanks": "tanks", "Commercial": "commercial" };
const TAB_KEYS = ["all", "sheds", "tanks", "commercial"];

export const Gallery = () => {
  const { t } = useI18n();
  const [tab, setTab] = useState("all");
  const items = tab === "all" ? GALLERY : GALLERY.filter((g) => CAT_KEY[g.cat] === tab);

  return (
    <section id="portfolio" className="bg-surfacealt py-16 sm:py-20 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionLabel index="/ 03">{t.labels.portfolio}</SectionLabel>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <h2 className="font-display font-700 uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-charcoal max-w-xl">
              {t.gallery.heading[0]} <span className="text-safety">{t.gallery.heading[1]}</span>
            </h2>

            <div className="flex flex-wrap gap-2" data-testid="gallery-tabs">
              {TAB_KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`gallery-tab-${k}`}
                  className={`font-mono2 text-xs font-700 uppercase tracking-wider px-4 h-10 border-2 transition-colors ${
                    tab === k
                      ? "bg-charcoal text-white border-charcoal"
                      : "bg-transparent text-charcoal border-charcoal/30 hover:border-charcoal"
                  }`}
                >
                  {t.gallery.tabs[k]}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.div
                layout
                key={g.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`gallery-item-${g.id}`}
                className="group relative overflow-hidden border-2 border-charcoal bg-white"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={g.img}
                    alt={t.gallery.titles[g.id]}
                    className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                  />
                  <span className="absolute top-3 left-3 font-mono2 text-[10px] font-700 uppercase tracking-wider bg-safety text-white px-2.5 py-1">
                    {t.gallery.tabs[CAT_KEY[g.cat]]}
                  </span>
                  {g.real && (
                    <span className="absolute top-3 right-3 font-mono2 text-[10px] font-700 uppercase tracking-wider bg-charcoal text-safety px-2.5 py-1 border border-safety">
                      {t.gallery.actualWork}
                    </span>
                  )}
                </div>
                <div className="p-5 border-t-2 border-charcoal">
                  <h3 className="font-display font-700 uppercase text-xl text-charcoal mb-3 leading-none">
                    {t.gallery.titles[g.id]}
                  </h3>
                  <div className="flex items-center justify-between font-mono2 text-xs text-charcoal/60">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={13} className="text-safety" /> {g.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} className="text-safety" /> {g.tat}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
